// Global Instructions Rule Applied!
//
// Consolidated fund "Capital Calls" tab — MIRRORS the fund full-form
// Capital Calls tab section-for-section (owner ruling 2026-08-08: the
// group form uses the same UI as a normal fund wherever it makes sense):
// Position → Capital Calls → Intercompany Transfers (group-specific) →
// Equalization (the SHARED EqualizationSection pointed at the primary
// fund — the backend weighs LPs by gross subscription there).
// A satellite on the 'Reallocate fees to date' late-closer policy (an SBIC:
// no catch-up fee) adds a Late-joiner Fee Reallocation section last
// (FundGroupFeeReallocationSection; renders nothing otherwise).
//
// A consolidated call is created and edited ENTIRELY in the STANDARD
// Capital Call form (owner ruling 2026-08-08: the separate plan modal was
// confusing and is gone) — "New Consolidated Call" opens the form
// pre-anchored to the primary fund + a fund_group_calls header via the
// fgc__ machinery; the form's consolidated banner carries the gross sizing
// (fixed amount or % of subscriptions, resolved against the SUBSCRIPTION
// pool). The LIFECYCLE stays here: "Preview split" computes the call,
// every non-fee line shared by subscription (the fee line stays
// per-LP engine truth, so an LP with a side-letter fee break pays a
// smaller total) — per-investor amounts a person edited on the call
// form are kept by Preview split and Issue (Recompute asks whether to
// keep or discard them); Issue /
// Revert / Delete run here; per-fund screens are read-only for group-linked
// calls.
//
// Capital moves to the satellite via the Transfers section rendered below
// the calls grid (FundGroupTransfersSection). Since 2026-09-28 a transfer
// draws on the POOL of every issued call (the section's pool table) and is
// built in the standard Capital Call form; only a transfer sourced from an
// equalization call - or one booked before the pool model - stays anchored
// to a call. The grid shows one column per call item (investments /
// expenses / management fees / gross) plus % allocated, and each row
// expands into an ALLOCATION MATRIX of the transfers anchored to it: the
// gross line, the primary fund's retained line, the satellite's transferred
// line, and one indented child row per booked transfer. The per-LP called
// split lives on the standard call form ("Open call form").

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Alert, Button, Dropdown, Modal, Space, Spin, Table, Tag, Typography } from 'antd'
import { DownOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import type { RootState } from '../../../state_manager/store.ts'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useNumberDisplay } from '../../../core/utils/inputNumberFormat.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_CAPITAL_CALLS } from '../../../state_manager/globals.ts'
import { FormSection } from '../../../core/components/FormSection.tsx'
import {
  FundGroupCallAllocationRow,
  FundGroupCallDetail,
  FundGroupCallRow
} from '../../../state_manager/data_models/fund_group.ts'
import {
  deleteFundGroupCall,
  fetchFundGroupCallDetail,
  fetchFundGroupCalls,
  issueFundGroupCall,
  refreshFundGroupCallSplit,
  revertFundGroupCall
} from '../services/fundGroupsService.ts'
import { FundGroupTransfersSection } from './FundGroupTransfersSection.tsx'
import { FundGroupFeeReallocationSection } from './FundGroupFeeReallocationSection.tsx'
import { GroupCallPositionView } from './GroupCallPositionView.tsx'
import EqualizationSection from '../../capital_calls/components/EqualizationSection.tsx'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'

const { Text } = Typography

interface Props {
  fundGroupId: string | null
  /** the group's primary fund — hosts the shared Equalization section */
  primaryFundId?: string | null
}

const STATUS_COLORS: Record<string, string> = {
  Draft: 'blue',
  Issued: 'blue'
}

export function FundGroupCallsTab({ fundGroupId, primaryFundId }: Props): React.ReactElement | null {
  const currencyText = useNumberDisplay('currency')
  const { openFormInTab } = useTabManagerContext()
  const groupName = useSelector(
    (s: RootState) => (s.globalConfigs?.fund_groups ?? []).find((g) => g.id === fundGroupId)?.name ?? 'Consolidated fund'
  )
  const [rows, setRows] = useState<FundGroupCallRow[]>([])
  const [loading, setLoading] = useState(false)
  const [acting, setActing] = useState(false)
  const [details, setDetails] = useState<Record<string, FundGroupCallDetail | null>>({})
  // static Modal.confirm does not render under React 19 - hook form required
  const [modalApi, modalContextHolder] = Modal.useModal()

  const load = useCallback(async () => {
    if (!fundGroupId) return
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundGroupCalls(fundGroupId, token)
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      setRows(res.data)
      setDetails({})
    } finally {
      setLoading(false)
    }
  }, [fundGroupId])

  useEffect(() => {
    void load()
  }, [load])

  const loadDetail = useCallback(async (fundGroupCallId: string) => {
    const token = getAccessToken(store.getState().auth)
    const res = await fetchFundGroupCallDetail(fundGroupCallId, token)
    if (res.error || !res.data) {
      showErrorToast(res.error || 'Failed to load consolidated call detail')
      return
    }
    setDetails((prev) => ({ ...prev, [fundGroupCallId]: res.data }))
  }, [])

  const runAction = useCallback(
    async (
      action: () => Promise<{ success: boolean; error?: string }>,
      successMessage: string
    ) => {
      setActing(true)
      try {
        const res = await action()
        if (!res.success) {
          showErrorToast(res.error || 'Action failed')
          return
        }
        toast.success(successMessage)
        await load()
      } finally {
        setActing(false)
      }
    },
    [load]
  )

  // "New Consolidated Call" goes STRAIGHT into the standard Capital Call
  // form (no plan modal): the form detects ?fund_group= and creates the
  // fund_group_calls header + materialised Draft call on first save.
  const handleNewCall = useCallback(() => {
    if (!fundGroupId || !primaryFundId) return
    openFormInTab(TABLE_CAPITAL_CALLS, null, 'New Consolidated Call', {
      fund: primaryFundId,
      fund_group: fundGroupId
    })
  }, [fundGroupId, primaryFundId, openFormInTab])

  const handlePreviewSplit = useCallback(
    async (call: FundGroupCallRow) => {
      setActing(true)
      try {
        const token = getAccessToken(store.getState().auth)
        const res = await refreshFundGroupCallSplit(call.fund_group_calls__id, token)
        if (!res.success) {
          showErrorToast(res.error || 'Failed to refresh the split')
          return
        }
        await loadDetail(call.fund_group_calls__id)
        toast.success('Split by consolidated commitment refreshed')
      } finally {
        setActing(false)
      }
    },
    [loadDetail]
  )

  const handleIssue = useCallback(
    (call: FundGroupCallRow) => {
      modalApi.confirm({
        title: call.fund_group_calls__is_equalization ? 'Issue equalization call?' : 'Issue consolidated call?',
        content: call.fund_group_calls__is_equalization
          ? 'This issues the catch-up lines as computed (each late closer\'s share of the earlier calls, ' +
            'weighed by consolidated commitment) and books the call on the primary fund ledger ' +
            'immediately. Once issued it can fund intercompany transfers.'
          : 'This issues the call as computed (fee lines from the fee engine, everything else ' +
            'pro-rata by consolidated commitment) and books it on the primary fund ledger ' +
            'immediately. The amounts on the call form are booked as they stand, including ' +
            'any edited there. A call changed since it was computed is refused: run Preview split first.',
        okText: 'Issue',
        onOk: () =>
          runAction(
            () => issueFundGroupCall(call.fund_group_calls__id, getAccessToken(store.getState().auth)),
            'Consolidated call issued'
          )
      })
    },
    [modalApi, runAction]
  )

  const handleRevert = useCallback(
    (call: FundGroupCallRow) => {
      modalApi.confirm({
        title: 'Revert to Draft?',
        content:
          (call.prog__paid_total ?? 0) > 0
            ? `This unbooks the primary-fund capital call and returns it to an editable Draft. ${currencyText(call.prog__paid_total ?? 0)} of LP payments currently applied to it stay booked and read as prepayments until it is reissued.`
            : 'This unbooks the primary-fund capital call and returns it to an editable Draft. Payments already recorded are kept and read as prepayments until it is reissued.',
        okText: 'Revert',
        onOk: () =>
          runAction(
            () => revertFundGroupCall(call.fund_group_calls__id, getAccessToken(store.getState().auth)),
            'Consolidated call reverted to Draft'
          )
      })
    },
    [modalApi, runAction, currencyText]
  )

  const handleDelete = useCallback(
    (call: FundGroupCallRow) => {
      modalApi.confirm({
        title: 'Delete draft consolidated call?',
        content: 'The plan and its draft primary-fund call are removed. Only Draft calls can be deleted.',
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: () =>
          runAction(
            () => deleteFundGroupCall(call.fund_group_calls__id, getAccessToken(store.getState().auth)),
            'Consolidated call deleted'
          )
      })
    },
    [modalApi, runAction]
  )

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'fund_group_calls__name',
        key: 'name',
        render: (v: string | null, r: FundGroupCallRow) => (
          <Space size={4}>
            <span>{v ?? '—'}</span>
            {r.fund_group_calls__is_equalization ? <Tag>Equalization</Tag> : null}
          </Space>
        )
      },
      {
        title: 'Call date',
        dataIndex: 'fund_group_calls__call_date_fmt',
        key: 'call_date',
        width: 110
      },
      {
        title: 'Due date',
        dataIndex: 'fund_group_calls__due_date_fmt',
        key: 'due_date',
        width: 110
      },
      {
        title: 'Investments',
        dataIndex: 'prog__investments_total',
        key: 'investments',
        align: 'right' as const,
        render: (v: number | null) => (v ? currencyText(v) : <Text type='secondary'>—</Text>)
      },
      {
        title: 'Expenses',
        dataIndex: 'prog__expenses_total',
        key: 'expenses',
        align: 'right' as const,
        render: (v: number | null) => (v ? currencyText(v) : <Text type='secondary'>—</Text>)
      },
      {
        title: 'Mgmt fees',
        dataIndex: 'prog__fees_total',
        key: 'fees',
        align: 'right' as const,
        render: (v: number | null) => (v ? currencyText(v) : <Text type='secondary'>—</Text>)
      },
      {
        // the call's actual total (the sum of what every LP is asked); the
        // gross ask (% of subscriptions or the fixed amount) is the hint,
        // shown when side-letter fee breaks make the two differ
        title: 'Total called',
        dataIndex: 'prog__gross_total',
        key: 'gross',
        align: 'right' as const,
        render: (v: number | null, r: FundGroupCallRow) => {
          const ask = r.prog__gross_ask ?? null
          const askDiffers = ask != null && v != null && Math.abs(ask - v) >= 0.005
          const pct =
            r.fund_group_calls__total_percentage != null
              ? `${(r.fund_group_calls__total_percentage * 100).toFixed(2)}% of consolidated commitments`
              : null
          const hint = [pct, askDiffers ? `ask ${currencyText(ask)}` : null].filter(Boolean).join(' · ')
          return (
            <span>
              <Text strong>{currencyText(v ?? 0)}</Text>
              {hint ? <Text type='secondary'> ({hint})</Text> : null}
            </span>
          )
        }
      },
      {
        title: '% allocated',
        dataIndex: 'prog__allocated_pct',
        key: 'allocated',
        align: 'right' as const,
        width: 110,
        render: (v: number | null, r: FundGroupCallRow) =>
          (r.prog__transferred_total ?? 0) > 0 && v != null ? (
            `${(v * 100).toFixed(1)}%`
          ) : r.fund_group_call_statuses__label === 'Issued' ? (
            '0.0%'
          ) : (
            <Text type='secondary'>—</Text>
          )
      },
      {
        title: 'Status',
        dataIndex: 'fund_group_call_statuses__label',
        key: 'status',
        width: 100,
        render: (v: string | null) => <Tag color={STATUS_COLORS[v ?? ''] ?? 'default'}>{v ?? '—'}</Tag>
      },
      {
        title: 'Primary fund call',
        key: 'primary_call',
        render: (_: unknown, r: FundGroupCallRow) =>
          r.capital_calls__id ? (
            <Button
              type='link'
              size='small'
              style={{ padding: 0 }}
              onClick={() =>
                openFormInTab(
                  TABLE_CAPITAL_CALLS,
                  r.capital_calls__id,
                  r.capital_calls__name ?? 'Capital Call'
                )
              }
            >
              {r.capital_calls__name ?? 'Capital call'}
            </Button>
          ) : (
            <Text type='secondary'>not materialised</Text>
          )
      },
      {
        title: '',
        key: 'actions',
        width: 110,
        render: (_: unknown, r: FundGroupCallRow) => {
          const status = r.fund_group_call_statuses__label
          const items = [
            ...(status === 'Draft'
              ? [
                  ...(r.capital_calls__id ? [{ key: 'open-call', label: 'Open call form' }] : []),
                  // an equalization call has no subscription split to preview:
                  // its catch-up lines compute on the call form
                  ...(r.fund_group_calls__is_equalization
                    ? []
                    : [{ key: 'preview-split', label: 'Preview split' }]),
                  { key: 'issue', label: 'Issue' },
                  { key: 'delete', label: 'Delete', danger: true }
                ]
              : []),
            ...(status === 'Issued'
              ? [
                  ...(r.capital_calls__id ? [{ key: 'open-call', label: 'Open call form' }] : []),
                  { key: 'revert', label: 'Revert to Draft', danger: true }
                ]
              : [])
          ]
          if (items.length === 0) return null
          return (
            <Dropdown
              disabled={acting}
              menu={{
                items,
                onClick: ({ key }) => {
                  if (key === 'open-call' && r.capital_calls__id)
                    openFormInTab(
                      TABLE_CAPITAL_CALLS,
                      r.capital_calls__id,
                      r.capital_calls__name ?? 'Capital Call'
                    )
                  if (key === 'preview-split') void handlePreviewSplit(r)
                  if (key === 'issue') handleIssue(r)
                  if (key === 'delete') handleDelete(r)
                  if (key === 'revert') handleRevert(r)
                }
              }}
            >
              <Button size='small'>
                Actions <DownOutlined />
              </Button>
            </Dropdown>
          )
        }
      }
    ],
    [acting, currencyText, handleDelete, handleIssue, handlePreviewSplit, handleRevert, openFormInTab]
  )

  // allocation matrix: gross row / retained + transferred fund rows / one
  // indented child row per booked transfer
  const allocationColumns = useMemo(
    () => [
      {
        title: 'Allocation',
        key: 'label',
        render: (_: unknown, a: FundGroupCallAllocationRow) =>
          a.prog__kind === 'transfer' ? (
            <span style={{ paddingLeft: 24 }}>
              <Text type='secondary'>{a.prog__label ?? '—'}</Text>{' '}
              {a.prog__status ? <Tag color='green'>{a.prog__status}</Tag> : null}
            </span>
          ) : a.prog__kind === 'gross' ? (
            <Text strong>{a.prog__label ?? '—'}</Text>
          ) : (
            <Text>{a.prog__label ?? '—'}</Text>
          )
      },
      {
        title: 'Investments',
        dataIndex: 'prog__investments',
        key: 'investments',
        align: 'right' as const,
        render: (v: number | null, a: FundGroupCallAllocationRow) =>
          v ? (
            a.prog__kind === 'gross' ? <Text strong>{currencyText(v)}</Text> : currencyText(v)
          ) : (
            <Text type='secondary'>—</Text>
          )
      },
      {
        title: 'Expenses',
        dataIndex: 'prog__expenses',
        key: 'expenses',
        align: 'right' as const,
        render: (v: number | null, a: FundGroupCallAllocationRow) =>
          v ? (
            a.prog__kind === 'gross' ? <Text strong>{currencyText(v)}</Text> : currencyText(v)
          ) : (
            <Text type='secondary'>—</Text>
          )
      },
      {
        title: 'Mgmt fees',
        dataIndex: 'prog__fees',
        key: 'fees',
        align: 'right' as const,
        render: (v: number | null, a: FundGroupCallAllocationRow) =>
          v ? (
            a.prog__kind === 'gross' ? <Text strong>{currencyText(v)}</Text> : currencyText(v)
          ) : (
            <Text type='secondary'>—</Text>
          )
      },
      {
        title: 'Total',
        dataIndex: 'prog__total',
        key: 'total',
        align: 'right' as const,
        render: (v: number | null) => <Text strong>{currencyText(v ?? 0)}</Text>
      },
      {
        title: '% of call',
        dataIndex: 'prog__pct',
        key: 'pct',
        align: 'right' as const,
        width: 100,
        render: (v: number | null) =>
          v != null ? `${(v * 100).toFixed(1)}%` : <Text type='secondary'>—</Text>
      }
    ],
    [currencyText]
  )

  const renderDetail = useCallback(
    (r: FundGroupCallRow) => {
      const detail = details[r.fund_group_calls__id]
      if (detail === undefined) {
        return (
          <div className='flex justify-center p-4'>
            <Spin size='small' />
          </div>
        )
      }
      if (!detail) return <Text type='secondary'>Failed to load detail.</Text>
      const allocation = detail.allocation ?? []
      const grossTotal = allocation.find((a) => a.prog__kind === 'gross')?.prog__total ?? 0
      if (allocation.length === 0 || !grossTotal) {
        return (
          <Alert
            type='info'
            showIcon
            message='Allocation not available yet'
            description='The call has no computed amounts yet. Run “Preview split” to compute it; the per-investor split lives on the call form (“Open call form”).'
          />
        )
      }
      return (
        <div className='flex flex-col gap-2'>
          <Table
            size='small'
            pagination={false}
            rowKey={(a: FundGroupCallAllocationRow) =>
              a.fund_group_transfers__id ?? `${a.prog__kind}-${a.funds__id ?? 'gross'}`
            }
            dataSource={allocation}
            columns={allocationColumns}
            scroll={{ x: 'max-content' }}
            onRow={(a: FundGroupCallAllocationRow) =>
              a.prog__kind === 'gross' ? { style: { background: 'rgba(0, 0, 0, 0.03)' } } : {}
            }
          />
          <Text type='secondary'>
            Retained = gross − transfers booked against this call. The per-investor called split
            lives on the call form (“Open call form”).
          </Text>
        </div>
      )
    },
    [allocationColumns, details]
  )

  if (!fundGroupId) return null

  return (
    <div className='h-full overflow-y-auto p-3 flex flex-col gap-3'>
      {modalContextHolder}

      <FormSection title='Position' titleColor='purple'>
        <GroupCallPositionView fundGroupId={fundGroupId} />
      </FormSection>

      <FormSection
        title='Capital Calls'
        titleColor='blue'
        loading={loading}
        headerActions={
          <Space>
            <ExportRowsButton
              name={`${groupName} Capital Calls`}
              rows={rows}
              columns={[
                { title: 'Name', value: (r) => r.fund_group_calls__name },
                { title: 'Equalization', value: (r) => (r.fund_group_calls__is_equalization ? 'Yes' : null) },
                { title: 'Call date', value: (r) => r.fund_group_calls__call_date, kind: 'date' },
                { title: 'Due date', value: (r) => r.fund_group_calls__due_date, kind: 'date' },
                { title: 'Investments', value: (r) => r.prog__investments_total },
                { title: 'Expenses', value: (r) => r.prog__expenses_total },
                { title: 'Mgmt fees', value: (r) => r.prog__fees_total },
                { title: 'Total called', value: (r) => r.prog__gross_total },
                { title: '% of commitments', value: (r) => r.fund_group_calls__total_percentage, kind: 'percent' },
                {
                  // the Total called hint: the gross ask only where fee breaks make it differ
                  title: 'Gross ask',
                  value: (r) =>
                    r.prog__gross_ask != null &&
                    r.prog__gross_total != null &&
                    Math.abs(r.prog__gross_ask - r.prog__gross_total) >= 0.005
                      ? r.prog__gross_ask
                      : null
                },
                {
                  // as the grid reads it: 0% on an Issued call nothing is transferred from yet
                  title: '% allocated',
                  value: (r) =>
                    (r.prog__transferred_total ?? 0) > 0 && r.prog__allocated_pct != null
                      ? r.prog__allocated_pct
                      : r.fund_group_call_statuses__label === 'Issued'
                        ? 0
                        : null,
                  kind: 'percent'
                },
                { title: 'Status', value: (r) => r.fund_group_call_statuses__label },
                { title: 'Primary fund call', value: (r) => r.capital_calls__name }
              ]}
            />
            <Button
              type='primary'
              size='small'
              icon={<PlusOutlined />}
              onClick={handleNewCall}
              disabled={!primaryFundId}
            >
              New Consolidated Call
            </Button>
            <Button icon={<ReloadOutlined />} size='small' onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Text type='secondary' className='block mb-2'>
          One fully-specced gross call from the primary fund — create and edit it in the standard
          call form (sizing lives in its consolidated banner), then Preview split / Issue from here.
          Equalization calls drafted below list here too. Capital moves via Transfers below.
        </Text>
        <Table
          size='small'
          pagination={false}
          rowKey={(r: FundGroupCallRow) => r.fund_group_calls__id}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 'max-content' }}
          expandable={{
            expandedRowRender: renderDetail,
            onExpand: (expanded, r: FundGroupCallRow) => {
              if (expanded && details[r.fund_group_calls__id] === undefined) {
                void loadDetail(r.fund_group_calls__id)
              }
            }
          }}
          locale={{ emptyText: 'No consolidated calls yet' }}
        />
      </FormSection>

      <FundGroupTransfersSection fundGroupId={fundGroupId} />

      {primaryFundId ? (
        <>
          <Alert
            type='warning'
            showIcon
            message='Owner sign-off required before issuing an equalization call'
            description='Deltas below are computed at group level — every LP weighs as their gross Consolidated Commitment, exactly how consolidated calls split. Draft via Create Equalization Call, review each line’s mode on the Draft, and issue only once signed off.'
          />
          <EqualizationSection fundId={primaryFundId} onDraftCreated={() => void load()} />
        </>
      ) : null}

      <FundGroupFeeReallocationSection fundGroupId={fundGroupId} />
    </div>
  )
}

export default FundGroupCallsTab
